import { type FC, type PropsWithChildren } from 'react';
import clsx from 'clsx';

import { Sidebar } from '../Sidebar';
import { Title } from '../Title';
import { Actions } from '../Actions';
import { SidebarProvider, useSidebar } from '../../contexts/SidebarContext';
// El ConfigProvider de Antd (tema + locale es_ES) vive ahora en Main.tsx,
// a nivel raíz, así que también cubre el login y los modales que no
// están dentro de un <Screen>. El tema claro/oscuro en sí lo gestiona
// ThemeContext a través de la clase `dark` en <html>, que es lo que
// activan las clases `dark:` de Tailwind usadas en este componente.

interface Action {
  disabled?: boolean
  icon: any
  onClick: () => void
}

type Props = PropsWithChildren<{
  actions?: Action[]
  id: string
  sidebar?: boolean
  title?: string
  hideLabel?: boolean
}>;

// SidebarProvider envuelve el contenido para que <Sidebar /> y el resto
// de este componente compartan el mismo estado "isOpen": antes el
// sidebar gestionaba su propio ancho (80px/288px) mientras que aquí se
// aplicaba un margen fijo de 60px que no coincidía con ninguno de los
// dos, así que el contenido nunca quedaba realmente alineado con él.
const Screen: FC<Props> = (props) => (
  <SidebarProvider>
    <ScreenContent {...props} />
  </SidebarProvider>
);

const ScreenContent: FC<Props> = ({ hideLabel, actions, children, id, sidebar = true, title }) => {
  const { isOpen } = useSidebar();

  return (
      <div
        className={clsx(
          'min-h-screen relative transition-all duration-500 ease-in-out',
          // Light mode background with animated gradient
          'bg-gradient-to-br from-gray-50 via-white to-gray-100',
          // Dark mode background with animated gradient
          'dark:bg-gradient-to-br dark:from-background dark:via-surface-dark dark:to-background',
          // Animated shadow effect
          'before:absolute before:inset-0 before:opacity-0 dark:before:opacity-100 before:transition-opacity before:duration-500',
          'before:bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.1),transparent_50%)]',
          'after:absolute after:inset-0 after:opacity-0 dark:after:opacity-100 after:transition-opacity after:duration-500',
          'after:bg-[radial-gradient(circle_at_70%_80%,rgba(99,102,241,0.1),transparent_50%)]',
          // Animated floating shadows
          'relative overflow-hidden'
        )}
        id={id}
      >
        {/* Animated background elements - floating orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={clsx(
            'absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl transition-all duration-700',
            'bg-gradient-to-r from-blue-400 to-purple-400',
            'opacity-20 dark:opacity-10',
            'animate-float'
          )} />
          <div className={clsx(
            'absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl transition-all duration-700',
            'bg-gradient-to-r from-purple-400 to-pink-400',
            'opacity-20 dark:opacity-10',
            'animate-float-reverse'
          )} />
        </div>

        {sidebar && (
          <div className="relative z-[var(--z-sidebar)] pointer-events-none">
            <div className="pointer-events-auto">
              <Sidebar />
            </div>
          </div>
        )}

        <div className={clsx(
          'relative z-10 min-h-[calc(100vh-60px)] py-8 px-1 transition-all duration-500',
          'md:p-8',
          // El margen izquierdo sigue el ancho real del sidebar
          // (w-20 = 80px colapsado, w-72 = 288px expandido), en vez de
          // un valor fijo de 60px que no coincidía con ninguno de los dos.
          sidebar && (isOpen ? 'md:ml-72' : 'md:ml-20'),
          // Mobile adjustments
          'md:min-h-[calc(100vh-60px)]',
          // Add bottom padding when label is shown
          !hideLabel && 'pb-16 md:pb-20'
        )}>
          {/* Title ahora es `sticky` y ocupa su propio espacio en el
              flujo normal, así que ya no hace falta compensar su altura
              con márgenes negativos/fijos en los hermanos. */}
          {title && <Title>{title}</Title>}
          <div className={clsx(
            'flex flex-col transition-all duration-500',
            title && 'mt-6'
          )}>
            {children}
          </div>
        </div>

        {!hideLabel && (
          <div className={clsx(
            'fixed bottom-0 w-full flex justify-end md:justify-end items-center z-[var(--z-fixed-bar)]',
            'bg-white/80 dark:bg-surface-dark/80 backdrop-blur-sm',
            'border-t border-gray-200 dark:border-borders',
            'transition-all duration-500',
            'py-2.5 px-4 md:px-8',
            // Mobile centering
            'md:justify-end justify-center'
          )}>
            <p className={clsx(
              'text-xs md:text-sm font-semibold tracking-wide transition-colors duration-300',
              'text-gray-700 dark:text-surface-light',
              'mr-0 md:mr-[2%]'
            )}>
              <span className="text-gray-900 dark:text-white transition-colors duration-300">
                Mallorca Oposita
              </span>
              {' '}powered by{' '}
              <a
                href="https://unbrokenapp.com"
                target='_blank'
                rel="noreferrer"
                className={clsx(
                  'text-gray-900 dark:text-white font-extrabold transition-all duration-300',
                  'hover:text-blue-600 dark:hover:text-blue-400',
                  'hover:underline underline-offset-2'
                )}
              >
                UNBROKEN
              </a>
            </p>
          </div>
        )}

        {actions && actions.length > 0 && (
          <div className={clsx(
            'fixed right-8 bottom-8 md:right-8 md:bottom-8 z-[var(--z-fixed-bar)]',
            'flex justify-end'
          )}>
            <Actions actions={actions} />
          </div>
        )}
      </div>
  );
};

export { Screen };
